'use client';

import { Stack, Box, token, Grid, GridItem } from '@terra/ui-styled-system';
import { Search01Icon } from '@terra/ui-icons';
import type { ChangeEvent, ReactNode } from 'react';
import { Fragment, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Input } from '../../components/input';
import { Skeleton } from '../../components/skeleton';
import { Text } from '../../components/text';
import { Tooltip } from '../../components/tooltip';
import { EmptyState } from '../empty-state/EmptyState';
import { FilterBarEmptyState } from '../../components/filter-bar';
import { useFooterScrollShadow } from '../../hooks/useFooterScrollShadow';

const DEFAULT_COLUMN_WIDTH = 'minmax(0, 1fr)';
const renderTableGridCell = (cell: ReactNode) => {
  if (typeof cell === 'string' || typeof cell === 'number') {
    const text = String(cell);

    return (
      <Tooltip title={text}>
        <Box minW={0} truncate>
          <Text fontSize="sm" truncate>
            {text}
          </Text>
        </Box>
      </Tooltip>
    );
  }

  return cell;
};

export type TableGridColumn = {
  id: string;
  title: ReactNode;
  width?: string;
  truncate?: boolean;
  align?: 'start' | 'end' | 'center';
  cellPx?: string;
  hideHeaderDividerAfter?: boolean;
};
export type TableGridRow = {
  id: string;
  cells: ReactNode[];
  value: string;
  groupId?: string;
  variant?: 'default' | 'subtle';
};
const filterRowsBySearch = ({
  rows,
  searchQuery,
}: {
  rows: TableGridRow[];
  searchQuery: string;
}): TableGridRow[] => {
  const trimmedQuery = searchQuery.trim();

  if (trimmedQuery === '') {
    return rows;
  }

  const normalizedQuery = trimmedQuery.toLowerCase();
  const matchingGroupIds = new Set<string>();

  for (const row of rows) {
    if (
      row.value.toLowerCase().includes(normalizedQuery) &&
      row.groupId !== undefined
    ) {
      matchingGroupIds.add(row.groupId);
    }
  }

  return rows.filter((row) => {
    if (row.value.toLowerCase().includes(normalizedQuery)) {
      return true;
    }

    return row.groupId !== undefined && matchingGroupIds.has(row.groupId);
  });
};

export type TableGridProps = {
  columns: TableGridColumn[];
  rows: TableGridRow[];
  placeholder: string;
  tableMaxHeight?: string;
  isLoading?: boolean;
  emptyState?: ReactNode;
  rightContent?: ReactNode;
};
export const TableGrid = ({
  columns,
  rows,
  placeholder,
  tableMaxHeight = '175px',
  isLoading,
  emptyState,
  rightContent,
}: TableGridProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const t = useTranslations('designSystem.tableGrid');
  const { scrollableRef, scrollContentRef, boxShadow, updateScrollState } =
    useFooterScrollShadow({
      boxShadow: `inset 0 -8px 8px -8px ${token('colors.neutral.a3')}`,
    });
  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const rowsToDisplay = useMemo(
    () => filterRowsBySearch({ rows, searchQuery }),
    [rows, searchQuery],
  );
  const columnCount = columns.length;
  const gridTemplateColumns = useMemo(() => {
    const hasCustomWidths = columns.some(
      (column) => column.width !== undefined,
    );

    if (!hasCustomWidths) {
      return `repeat(${columnCount}, ${DEFAULT_COLUMN_WIDTH})`;
    }

    return columns
      .map((column) => column.width ?? DEFAULT_COLUMN_WIDTH)
      .join(' ');
  }, [columnCount, columns]);
  const emptyStateComponent = emptyState || (
    <EmptyState title={t('emptyState.title')} />
  );

  if (columnCount === 0) {
    return null;
  }

  return (
    <Stack gap="0">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap="3"
        mb="3"
      >
        <Box position="relative" w="320px" maxW="full" flexShrink={0}>
          <Box
            position="absolute"
            left="2"
            top="50%"
            transform="translateY(-50%)"
          >
            <Search01Icon size={16} color={token('colors.fg.neutral.muted')} />
          </Box>
          <Input
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={placeholder}
            size="sm"
            paddingLeft="8"
            type="search"
          />
        </Box>
        {rightContent ? <Box flexShrink={0}>{rightContent}</Box> : null}
      </Box>
      <Grid
        w="full"
        columnGap="4"
        alignItems="center"
        style={{ gridTemplateColumns }}
      >
        {columns.map((column, index) => (
          <GridItem
            key={column.id}
            minW={0}
            px={column.cellPx ?? '3'}
            py="3.5"
            fontSize="xs"
            color="fg.neutral.subtle"
            display={column.align ? 'flex' : undefined}
            justifyContent={
              column.align === 'end'
                ? 'flex-end'
                : column.align === 'center'
                  ? 'center'
                  : undefined
            }
            {...(index > 0 &&
              !columns[index - 1]?.hideHeaderDividerAfter && {
                position: 'relative',
                _before: {
                  content: '""',
                  position: 'absolute',
                  left: '0',
                  top: '50%',
                  width: '1px',
                  height: '16px',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'border.neutral.default',
                },
              })}
          >
            {column.title}
          </GridItem>
        ))}
      </Grid>
      <Box height="1px" backgroundColor="border.neutral.subtle" />
      <Box
        ref={scrollableRef}
        overflowY="auto"
        onScroll={updateScrollState}
        style={{ maxHeight: tableMaxHeight, boxShadow }}
      >
        <Box ref={scrollContentRef}>
          {isLoading ? (
            <Box p="1">
              <Skeleton style={{ height: '150px' }} />
            </Box>
          ) : rows.length === 0 ? (
            emptyStateComponent
          ) : rowsToDisplay.length === 0 ? (
            <FilterBarEmptyState />
          ) : (
            rowsToDisplay.map((row, rowIndex) => (
              <Fragment key={row.id}>
                {rowIndex > 0 ? (
                  <Box height="1px" backgroundColor="border.neutral.subtle" />
                ) : null}
                <Grid
                  w="full"
                  columnGap="4"
                  alignItems="center"
                  bg={
                    row.variant === 'subtle' ? 'bg.neutral.subtle' : undefined
                  }
                  style={{ gridTemplateColumns }}
                >
                  {columns.map((column, columnIndex) => {
                    const cell = row.cells[columnIndex] ?? null;

                    return (
                      <GridItem
                        key={column.id}
                        minW={0}
                        px={column.cellPx ?? '3'}
                        py="2.5"
                        fontSize="sm"
                        color="fg.neutral.default"
                        display={column.align ? 'flex' : undefined}
                        justifyContent={
                          column.align === 'end'
                            ? 'flex-end'
                            : column.align === 'center'
                              ? 'center'
                              : undefined
                        }
                        overflow={
                          column.truncate === false ? 'visible' : 'hidden'
                        }
                        {...(column.truncate !== false && { truncate: true })}
                      >
                        {renderTableGridCell(cell)}
                      </GridItem>
                    );
                  })}
                </Grid>
              </Fragment>
            ))
          )}
        </Box>
      </Box>
    </Stack>
  );
};
